import IconSkeleton from '@/components/icons/base/IconSkeleton';
import dynamic from 'next/dynamic';
import * as React from 'react';
import { MATERIAL_SYMBOL_ICONS } from 'shared/custom/constants/commonConstants';
import type { IconProps } from './base/IconBase';
import { iconLoaders } from '@/components/icons/iconLoaders';

export type IconName = (typeof MATERIAL_SYMBOL_ICONS)[keyof typeof MATERIAL_SYMBOL_ICONS];

export type IconComponent = React.ComponentType<IconProps>;

type Props = IconProps & {
	name: IconName | '';
	fallback?: React.ReactNode;
};

const registry = new Map<IconName, IconComponent>();

const getDynamicIcon = (name: IconName, fallback?: React.ReactNode): IconComponent => {
	const cached = registry.get(name);
	if (cached) return cached;

	const Component = dynamic(iconLoaders[name], {
		ssr: false,
		loading: () => <>{fallback ?? <IconSkeleton />}</>,
	}) as IconComponent;

	registry.set(name, Component);
	return Component;
};

const DynamicIcon = ({ name, fallback = null, ...iconProps }: Props) => {
	if (!name) return null;

	const Icon = React.useMemo(() => getDynamicIcon(name, fallback), [name, fallback]);

	return <Icon {...iconProps} />;
};

export default DynamicIcon;
